import { useCallback, useEffect, useState } from "react";
import { AdaptiveGrid, Section, SectionHeader, StatusState } from "../adaptive/primitives";
import type { CrmBusiness, CrmInsight, CrmOpportunity } from "./types";
import type { CrmApiClient } from "./api";

function dateTime(value: string): string {
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function InsightList({ title, items }: { title: string; items: readonly string[] }) {
  if (items.length === 0) return null;
  return <section className="crm-insight-list"><h4>{title}</h4><ul>{items.map((item, index) => <li key={`${title}-${index}`}>{item}</li>)}</ul></section>;
}

export function CrmInsightsPanel({
  api,
  business,
  opportunities,
  onOpportunityCreated,
}: {
  api: CrmApiClient;
  business: CrmBusiness;
  opportunities: readonly CrmOpportunity[];
  onOpportunityCreated: () => void;
}) {
  const [insights, setInsights] = useState<readonly CrmInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.listInsights(business.id, 20);
      setInsights(result.insights);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo cargar la inteligencia comercial.");
    } finally {
      setLoading(false);
    }
  }, [api, business.id]);

  useEffect(() => { void load(); }, [load]);

  if (loading) return <StatusState tone="loading" title="Cargando inteligencia comercial…" message="Consultando auditorías y recomendaciones del negocio." />;
  if (error) return <StatusState tone="danger" title="No se pudo cargar la auditoría" message={error} action={<button onClick={() => void load()}>Reintentar</button>} />;
  if (insights.length === 0) return <StatusState title="Todavía no hay auditoría comercial" message="Cuando el negocio sea enriquecido desde Google Maps o un auditor comercial, el resumen y las recomendaciones aparecerán acá." />;

  const latest = insights[0]!;
  async function createOpportunity(productKey: string, reason: string | null) {
    setCreating(productKey);
    setError(null);
    try {
      await api.createOpportunity(business.id, {
        productKey,
        title: `${productKey} — ${business.name}`,
        stage: "NEW",
        source: `audit:${latest.source}`,
      });
      if (reason) {
        await api.addActivity(business.id, {
          type: "audit_recommendation_accepted",
          summary: `Oportunidad creada desde auditoría: ${productKey}`,
          details: { insightId: latest.id, productKey, reason },
        });
      }
      onOpportunityCreated();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "No se pudo crear la oportunidad.");
    } finally {
      setCreating(null);
    }
  }

  return <Section className="crm-insights" labelledBy="crm-insight-title">
    <SectionHeader
      titleId="crm-insight-title"
      eyebrow={`${latest.provider || latest.source}${latest.model ? ` · ${latest.model}` : ""}`}
      title="Inteligencia comercial"
      description={`Última auditoría: ${dateTime(latest.auditedAt)}`}
      actions={latest.documentUrl ? <a className="btn" href={latest.documentUrl} target="_blank" rel="noreferrer">Abrir informe</a> : undefined}
    />
    {error && <div className="inline-alert" role="alert">{error}</div>}
    <article className="details-card crm-insight-summary">
      <h3>Resumen</h3>
      <p>{latest.summary || "Sin resumen disponible."}</p>
    </article>
    <AdaptiveGrid compact={1} medium={2} wide={2}>
      <InsightList title="Fortalezas" items={latest.strengths} />
      <InsightList title="Riesgos" items={latest.risks} />
      <InsightList title="Hallazgos" items={latest.findings} />
      <InsightList title="Oportunidades detectadas" items={latest.opportunities} />
    </AdaptiveGrid>
    <section className="crm-recommendations">
      <h3>Productos recomendados</h3>
      {latest.recommendations.length === 0
        ? <p className="crm-muted-text">La auditoría no generó recomendaciones de producto.</p>
        : <AdaptiveGrid compact={1} medium={2} wide={3}>{latest.recommendations.map((recommendation) => {
          const existing = opportunities.find((opportunity) => opportunity.productKey === recommendation.productKey && opportunity.stage !== "LOST");
          return <article className="crm-card" key={`${latest.id}-${recommendation.productKey}`}>
            <div className="crm-card-head"><span className={`crm-pill ${recommendation.priority === "high" ? "urgent" : recommendation.priority === "medium" ? "high" : "normal"}`}>{recommendation.priority || "recomendado"}</span>{existing && <span className="crm-pill success">Ya en pipeline</span>}</div>
            <h3>{recommendation.productKey}</h3>
            <p>{recommendation.reason || "Recomendación generada por la auditoría comercial."}</p>
            {recommendation.evidence.length > 0 && <ul>{recommendation.evidence.map((item, index) => <li key={index}>{item}</li>)}</ul>}
            {!existing && <button className="primary-button" disabled={creating === recommendation.productKey} onClick={() => void createOpportunity(recommendation.productKey, recommendation.reason)}>{creating === recommendation.productKey ? "Creando…" : "Crear oportunidad"}</button>}
          </article>;
        })}</AdaptiveGrid>}
    </section>
    <InsightList title="Próximos pasos sugeridos" items={latest.nextSteps} />
    {insights.length > 1 && <details className="crm-insight-history"><summary>Ver historial de auditorías ({insights.length})</summary><ol>{insights.map((insight) => <li key={insight.id}><strong>{dateTime(insight.auditedAt)}</strong> · {insight.provider || insight.source} — {insight.summary || "Sin resumen"}</li>)}</ol></details>}
  </Section>;
}
